import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk'
import { watch } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/store/user/userStore'
import { resolveFaro } from '@@/package/config/resolveConfig'

// .client.ts suffix: this plugin runs in the browser only (not during SSR).
//
// Performance: only the lightweight Faro core (errors + web-vitals + session) is
// loaded eagerly, so load-time / hydration errors and Web Vitals are never missed.
// The heavy OpenTelemetry tracing bundle (@grafana/faro-web-tracing) is split into
// its own chunk and dynamically imported after the app is interactive (onNuxtReady),
// then attached via the documented faro.instrumentations.add() API. This keeps OTel
// out of the critical-path bundle without sacrificing early error/vitals capture.
export default defineNuxtPlugin(() => {
  const { public: pub } = useRuntimeConfig()

  // Resolve the collector from the live hostname, NOT from a build-time env var.
  // A single artifact is built once (on stage) and reused for opt/eu/kr, so a
  // baked URL would make opt POST to the stage collector and get CORS-blocked.
  // resolveFaro() maps each host to its own receiver at runtime instead.
  const { url, env } = resolveFaro(window.location.hostname)

  // Guard: skip initialization when there is no collector for this host
  // (local dev, CI preview builds, etc.).
  if (!url) return

  const faro = initializeFaro({
    url,
    app: {
      name: 'global-main-frontend',
      version: pub.faroAppVersion as string,
      environment: env,
    },
    // Core only: JS errors, web-vitals (LCP/CLS/INP), console logs, sessions.
    // Must be live before first paint/hydration so early errors aren't lost.
    instrumentations: getWebInstrumentations(),
    // No `trace.sampler` option — not supported in faro-web-sdk v2.x.
    // Default sampling is 100%, which is the desired behaviour for dev.
  })

  // Attribute every error / event / session to the logged-in user so RUM is
  // answerable to "WHICH user hit this?". The user store's `userV2.uuid` is the
  // single source of truth for identity (empty => anonymous). Watching it here
  // keeps auth flows untouched and covers initial load, login, and logout.
  const userStore = useUserStore()
  watch(
    () => userStore.userV2?.uuid,
    uuid => {
      if (uuid) {
        const u = userStore.userV2
        // PII policy: send only the opaque uuid as the correlation key. Do NOT send
        // email or fullName (real name) to the observability store — look up personal
        // details in the user DB by uuid when needed. `username` is the self-chosen
        // nickName only (not fullName); drop it too if treated as PII.
        faro.api.setUser({
          id: uuid,
          username: u.nickName || undefined,
          attributes: {
            numericId: String(u.id ?? ''),
            org: u.orgInfo?.orgName ?? '',
            locale: u.locale ?? '',
          },
        })
      } else {
        faro.api.resetUser()
      }
    },
    { immediate: true },
  )

  // Page-view / navigation tracking.
  // Faro core (getWebInstrumentations) ships errors + web-vitals + session, but has
  // NO SPA route-change instrumentation, so "which pages do users move through?"
  // is invisible. We add it explicitly: on every navigation we (1) setView() so
  // subsequent errors / vitals are attributed to the current view, and (2) push a
  // `page_view` custom event to Loki so navigation funnels are directly queryable.
  const router = useRouter()

  // Query strings can carry one-time secrets (OAuth `code`, password-reset tokens),
  // so we track the path + route name only — never the raw query — to keep secrets
  // out of the observability store. `fullPath` is used solely as an in-memory
  // dedupe key (never sent) to drop same-route replaces (e.g. router.replace to self).
  let lastTrackedPath = ''
  const trackPageView = (to: RouteLocationNormalized, from?: RouteLocationNormalized) => {
    if (to.fullPath === lastTrackedPath) return
    lastTrackedPath = to.fullPath
    const routeName = (to.name ?? to.path).toString()
    faro.api.setView({ name: routeName })
    faro.api.pushEvent(
      'page_view',
      {
        route: routeName,
        path: to.path,
        from_route: (from?.name ?? '').toString(),
        from_path: from?.path ?? '',
      },
      'navigation',
    )
  }

  // afterEach does not fire for the initial hydration navigation, so emit the
  // landing page once after the router settles, then track every SPA navigation.
  router.isReady().then(() => trackPageView(router.currentRoute.value))
  router.afterEach((to, from) => trackPageView(to, from))

  // Defer the heavy tracing instrumentation (OpenTelemetry) until the app is idle.
  // Dynamic import => separate chunk, kept out of the initial bundle.
  onNuxtReady(async () => {
    const { TracingInstrumentation } = await import('@grafana/faro-web-tracing')
    faro.instrumentations.add(
      new TracingInstrumentation({
        instrumentationOptions: {
          // Propagate W3C traceparent to backend origins so browser spans and
          // server spans share the same trace (full-stack tracing).
          propagateTraceHeaderCorsUrls: [
            /https:\/\/api-dev\.codepresso\.io/,
            /https:\/\/coderun-bridge-dev\.codepresso\.io/,
          ],
        },
      }),
    )
  })
})
